import { useState } from 'react';
import { createPortal } from 'react-dom';
import { CloseIcon, DownloadIcon, LoadingSpinner } from '@components/icons';
import type { Study } from '@skjv/common';
import { formatReference } from '@skjv/common';
import { generateStudyPdf } from '@/services/study-pdf';
import { encodeSkjv } from '@/services/skjv-codec';
import { bibleState } from '@/state/bible-state';

interface DownloadModalProps {
  study: Study;
  onClose: () => void;
}

/**
 * Generate a filename based on the study's passage and name.
 * E.g., "John 1:1 - John 1:5 -- In the Beginning"
 * or "John 1 -- In the Beginning" if no ending verse
 */
function generateFilename(study: Study): string {
  const startRef = formatReference(study.startingLocation);
  let passageRange: string;

  if (study.endingLocation) {
    const endRef = formatReference(study.endingLocation);
    passageRange = `${startRef} - ${endRef}`;
  } else {
    // No ending verse - just book and chapter
    passageRange = startRef.replace(/:\d+$/, ''); // Remove verse number
  }

  const safeName = study.name.replace(/[^a-zA-Z0-9\s-]/g, '').trim();
  return `${passageRange} -- ${safeName}`;
}

/**
 * Download modal - asks if user wants to include JSON with PDF.
 */
export function DownloadModal({ study, onClose }: DownloadModalProps) {
  const [includeJson, setIncludeJson] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      const filename = generateFilename(study);

      if (includeJson) {
        // Dynamically import JSZip
        const JSZip = (await import('jszip')).default;
        const zip = new JSZip();

        // Generate PDF
        const pdfBlob = await generateStudyPdf(study);
        zip.file(`${filename}.pdf`, pdfBlob);

        // Add hex-encoded .skjv backup (includes study group for portable import)
        const studyGroup = bibleState.getStudyGroup(study.studyGroupId);
        const exportData = { ...study, studyGroup };
        const skjvContent = encodeSkjv(exportData);
        zip.file(`${filename}.skjv`, skjvContent);

        // Generate and download .skjvz
        const zipBlob = await zip.generateAsync({ type: 'blob' });
        downloadBlob(zipBlob, `${filename}.skjvz`);
      } else {
        // Just download PDF
        const pdfBlob = await generateStudyPdf(study);
        downloadBlob(pdfBlob, `${filename}.pdf`);
      }

      onClose();
    } catch (error) {
      console.error('Download failed:', error);
      bibleState.showToast('Download failed. Please try again.', 'error');
    } finally {
      setIsDownloading(false);
    }
  };

  if (typeof document === 'undefined') return null;

  // Portaled to document.body: modals are application-modal, and a panel
  // ancestor's backdrop-filter would otherwise contain the fixed overlay.
  return createPortal(
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className="relative w-full max-w-md rounded-2xl bg-white p-6 shadow-xl dark:bg-stone-800">
        {/* Header */}
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-stone-800 dark:text-stone-200">
            Download Study
          </h2>
          <button
            onClick={onClose}
            className="p-1.5 text-stone-400 transition-colors hover:text-stone-600 dark:hover:text-stone-300"
          >
            <CloseIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="space-y-4">
          <p className="text-sm text-stone-600 dark:text-stone-400">
            Download "{study.name}" as a PDF file for printing.
          </p>

          {/* Include JSON checkbox */}
          <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-stone-200 p-3 transition-colors hover:bg-stone-50 dark:border-stone-700 dark:hover:bg-stone-700/50">
            <input
              type="checkbox"
              checked={includeJson}
              onChange={(e) => setIncludeJson(e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-stone-300 text-cyan-500 focus:ring-cyan-500"
            />
            <div>
              <span className="block font-medium text-stone-800 dark:text-stone-200">
                Include backup?
              </span>
              <span className="mt-0.5 block text-xs text-stone-500 dark:text-stone-400">
                Downloads as a .skjvz file with both PDF and backup for import later
              </span>
            </div>
          </label>

          {/* Filename preview */}
          <div className="rounded-lg bg-stone-100 p-3 dark:bg-stone-900/50">
            <span className="mb-1 block text-xs text-stone-500 dark:text-stone-400">Filename:</span>
            <span className="break-all font-mono text-sm text-stone-700 dark:text-stone-300">
              {generateFilename(study)}.{includeJson ? 'skjvz' : 'pdf'}
            </span>
          </div>
        </div>

        {/* Actions */}
        <div className="mt-6 flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 rounded-lg border border-stone-300 px-4 py-2 text-stone-600 transition-colors hover:bg-stone-100 dark:border-stone-600 dark:text-stone-400 dark:hover:bg-stone-700"
          >
            Cancel
          </button>
          <button
            onClick={handleDownload}
            disabled={isDownloading}
            className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-cyan-500 px-4 py-2 text-white transition-colors hover:bg-cyan-600 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isDownloading ? (
              <>
                <LoadingSpinner className="h-4 w-4 animate-spin" />
                Preparing...
              </>
            ) : (
              <>
                <DownloadIcon className="h-4 w-4" />
                Download
              </>
            )}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

/**
 * Helper to download a blob as a file.
 */
function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

// Re-export generateStudyPdf for use in print functionality
export { generateStudyPdf } from '@/services/study-pdf';

export default DownloadModal;
